import { useEffect, useMemo, useState } from "react";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import {
  areProviderTypesEqual,
  getAddressSignature,
  getAddressText,
  getInitialPrimaryFormState,
  mapProviderTypeOption,
  MAX_PROVIDER_TYPES,
  MIN_PROVIDER_TYPES,
  normalizeTextValue,
} from "../sections/primary/primarySectionUtils";

function getNextPageState(nextPageUrl) {
  if (!nextPageUrl) {
    return { limit: null, offset: null, showMore: false };
  }

  const parsedUrl = new URL(nextPageUrl);
  return {
    limit: parseInt(parsedUrl.searchParams.get("limit"), 10) || null,
    offset: parseInt(parsedUrl.searchParams.get("offset"), 10) || null,
    showMore: true,
  };
}

export default function useProviderDetailPrimarySection({
  canViewProviderTypes,
  providerInformation,
  setProviderInformation,
}) {
  const providerRepository = useService("repositories/provider-repository");
  const addressRepository = useService("repositories/address-repository");
  const enumRepository = useService("repositories/enum-repository");
  const [formState, setFormState] = useState(() => getInitialPrimaryFormState(providerInformation));
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [providerTypeState, setProviderTypeState] = useState({
    loading: false,
    options: [],
    limit: null,
    offset: null,
    showMore: false,
  });

  const initialFormState = useMemo(
    () => getInitialPrimaryFormState(providerInformation),
    [providerInformation]
  );

  useEffect(() => {
    setFormState(initialFormState);
  }, [initialFormState]);

  useEffect(() => {
    if (!isEditing || !canViewProviderTypes) {return undefined;}
    let cancelled = false;

    const loadProviderTypes = async () => {
      setProviderTypeState((prev) => ({ ...prev, loading: true }));
      try {
        const response = await enumRepository.query({
          urlPath: "core/dynamic-options/provider-type/",
          ...(search.trim() ? { search: search.trim() } : {}),
        });
        if (cancelled) {return;}
        const nextState = getNextPageState(await enumRepository.getNextPage());
        setProviderTypeState({
          loading: false,
          options: (response || []).map(mapProviderTypeOption),
          ...nextState,
        });
      } catch (error) {
        if (!cancelled) {getModalHandler().openFailureModal(error);}
      }
    };

    loadProviderTypes();
    return () => {
      cancelled = true;
    };
  }, [canViewProviderTypes, enumRepository, isEditing, search]);

  const hasProviderTypeError =
    formState.providerTypes.length < MIN_PROVIDER_TYPES ||
    formState.providerTypes.length > MAX_PROVIDER_TYPES;
  const isDirty =
    !areProviderTypesEqual(formState.providerTypes, initialFormState.providerTypes) ||
    normalizeTextValue(formState.email) !== normalizeTextValue(initialFormState.email) ||
    normalizeTextValue(formState.phone) !== normalizeTextValue(initialFormState.phone) ||
    normalizeTextValue(formState.homepage) !== normalizeTextValue(initialFormState.homepage) ||
    getAddressSignature(formState.address) !== getAddressSignature(initialFormState.address);

  const saveDisabled = !isDirty || hasProviderTypeError || isSaving;
  const providerTypeHelperText =
    formState.providerTypes.length < MIN_PROVIDER_TYPES
      ? `At least ${MIN_PROVIDER_TYPES} provider type must be selected.`
      : formState.providerTypes.length > MAX_PROVIDER_TYPES
        ? `Maximum of ${MAX_PROVIDER_TYPES} provider types can be selected.`
        : "";

  const updateField = (fieldName, value) => {
    setFormState((prev) => ({ ...prev, [fieldName]: value }));
  };

  const cancelEditing = () => {
    setFormState(initialFormState);
    setSearch("");
    setIsEditing(false);
  };

  const saveChanges = async () => {
    const providerId = providerInformation?.pk ?? providerInformation?.id;
    const addressId = providerInformation?.address?.id;
    const addressChanged =
      getAddressSignature(formState.address) !== getAddressSignature(initialFormState.address);
    if (!providerId || formState.providerTypes.length < MIN_PROVIDER_TYPES) {return;}
    if (addressChanged && !addressId) {
      getModalHandler().openFailureModal({ message: "Provider address record is missing." });
      return;
    }

    const payload = {};
    if (!areProviderTypesEqual(formState.providerTypes, initialFormState.providerTypes)) {
      payload.providerTypes = formState.providerTypes.map((item) => item.id);
    }
    ["email", "phone", "homepage"].forEach((fieldName) => {
      if (normalizeTextValue(formState[fieldName]) !== normalizeTextValue(initialFormState[fieldName])) {
        payload[fieldName] = formState[fieldName];
      }
    });

    setIsSaving(true);
    try {
      if (Object.keys(payload).length) {await providerRepository.patch(providerId, payload);}
      if (addressChanged && addressId) {
        await addressRepository.patch(addressId, {
          ...providerInformation.address,
          ...formState.address,
          raw: getAddressText(formState.address),
          formatted: getAddressText(formState.address),
        });
      }
      setProviderInformation((prev) => ({
        ...prev,
        email: formState.email,
        phone: formState.phone,
        homepage: formState.homepage,
        provider_types: formState.providerTypes,
        providerTypes: formState.providerTypes,
        address: prev?.address
          ? { ...prev.address, ...formState.address, raw: getAddressText(formState.address), formatted: getAddressText(formState.address) }
          : formState.address,
      }));
      setIsEditing(false);
      getModalHandler().openSuccessModal();
    } catch (error) {
      getModalHandler().openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  const loadMoreProviderTypes = async () => {
    if (!providerTypeState.limit || !providerTypeState.offset) {return;}
    setProviderTypeState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await enumRepository.query({
        urlPath: "core/dynamic-options/provider-type/",
        ...(search.trim() ? { search: search.trim() } : {}),
        limit: providerTypeState.limit,
        offset: providerTypeState.offset,
      });
      const nextState = getNextPageState(await enumRepository.getNextPage());
      setProviderTypeState((prev) => ({
        ...prev,
        loading: false,
        options: [...prev.options, ...(response || []).map(mapProviderTypeOption)],
        ...nextState,
      }));
    } catch (error) {
      getModalHandler().openFailureModal(error);
      setProviderTypeState((prev) => ({ ...prev, loading: false }));
    }
  };

  return {
    formState,
    isEditing,
    providerTypeHelperText,
    providerTypeState,
    saveDisabled,
    isSaving,
    search,
    setIsEditing,
    setSearch,
    updateField,
    cancelEditing,
    saveChanges,
    loadMoreProviderTypes,
  };
}
